import { type FC, useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetWorkflow, useListWorkflowExecutions, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowName, workflowsUrl, hasManualTrigger, isWorkflowDeleting, WorkflowDetailsTab } from "@/utils/automations";
import { WorkflowExecutionsList } from "@/components/automations/AutomationExecutionsList";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { useMembers } from "@/queries/organization-queries";
import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationPropertiesHeader } from "../AutomationPropertiesHeader";
import { AutomationDefinitionSection } from "./AutomationDefinitionSection";
import AutomationEditDialog from "@/components/automations/AutomationEditDialog";
import { currentWorkflowAtom, editableStepsAtom, isEditingStepsAtom, isWorkflowEditDialogOpenAtom } from "./state";
import {
    convertWorkflowActionToFormSteps,
    mergeFormDataIntoWorkflow,
    type WorkflowEditFormData,
} from "@/components/automations/automation-edit-form-data";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import { Button } from "@/components/flexkit/Button";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";
import { SaveAutomationStepsButton } from "@/routes/automations/automation-details-page/SaveAutomationStepsButton";
import { Banner } from "@/components/flexkit/Banner";
import { newWorkflowFromTemplate, type AutomationTemplate } from "@/utils/automation-templates";
import CreateAutomationButton from "@/routes/automations/automation-details-page/CreateAutomationButton";

const IconPencilWithSize = withGitpodSize(IconPencil);

export const AutomationDetailsPage: FC<{ template?: AutomationTemplate }> = ({ template }) => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const hasShownErrorToast = useRef(false);
    const { data: fetchedWorkflow, error: workflowError } = useGetWorkflow(template ? undefined : workflowId);
    const {
        data: workflowExecutions,
        error: workflowExecutionsError,
        refetch: refetchExecutions,
    } = useListWorkflowExecutions({
        enabled: !!workflowId,
        workflowId: workflowId || "",
    });
    const { data: members } = useMembers();
    const [selectedTab, setSelectedTab] = useState(
        template ? WorkflowDetailsTab.Definition : WorkflowDetailsTab.Executions,
    );
    const [workflow, setWorkflow] = useAtom(currentWorkflowAtom);
    const [isWorkflowEditDialogOpen, setIsWorkflowEditDialogOpen] = useAtom(isWorkflowEditDialogOpenAtom);
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const isEditingSteps = useAtomValue(isEditingStepsAtom);
    const location = useLocation();

    useEffect(() => {
        if (fetchedWorkflow) {
            setWorkflow(fetchedWorkflow);
        } else if (template) {
            setWorkflow(newWorkflowFromTemplate(template));
            setIsEditingSteps(true);
        }
    }, [fetchedWorkflow, template, setWorkflow, setIsEditingSteps]);

    // when leaving the page, turn off edit mode
    // so that when revisting the page, the edit mode is off
    useEffect(
        () => () => {
            setIsEditingSteps(false);
        },
        [setIsEditingSteps],
    );

    useEffect(() => {
        if (workflowExecutionsError && !hasShownErrorToast.current) {
            hasShownErrorToast.current = true;
            toast({
                title: "Failed to load executions",
                description: formatError(workflowExecutionsError),
            });
        }
        // Reset the flag when error is cleared
        if (!workflowExecutionsError && hasShownErrorToast.current) {
            hasShownErrorToast.current = false;
        }
    }, [workflowExecutionsError, toast]);

    useEffect(() => {
        switch (location.hash) {
            case `#${WorkflowDetailsTab.Definition}`:
                setSelectedTab(WorkflowDetailsTab.Definition);
                if (location.state?.edit) {
                    setIsEditingSteps(true);
                }
                break;
            case `#${WorkflowDetailsTab.Executions}`:
                setSelectedTab(WorkflowDetailsTab.Executions);
                break;
            default:
                if (template) {
                    setSelectedTab(WorkflowDetailsTab.Definition);
                } else {
                    // if no hash is set, navigate to the executions tab
                    navigate(`#${WorkflowDetailsTab.Executions}`, { replace: true });
                }
        }
    }, [location.hash, location.state?.edit, navigate, setIsEditingSteps, template]);

    const onAutomationEditDialogSubmit = (values: WorkflowEditFormData) => {
        if (workflow) {
            setWorkflow(mergeFormDataIntoWorkflow(workflow, values));
            setIsWorkflowEditDialogOpen(false);
        }
    };

    const changeTab = (value: string) => {
        navigate(`#${value}`, { replace: true });
    };

    const isDeleting = workflow ? isWorkflowDeleting(workflow) : false;

    return (
        <div data-testid="workflow-page" className="flex min-h-full flex-col gap-4">
            {workflow ? (
                <div className="flex flex-col gap-2">
                    {/* Header row: breadcrumb + action buttons */}
                    <div className="flex items-center justify-between">
                        <Breadcrumbs
                            customBreadcrumbs={[
                                { label: "Automations", to: workflowsUrl() },
                                { label: workflowName(workflow) },
                            ]}
                        />
                        <div className="flex items-center gap-2">
                            {/* We don't show the run action in the dropdown as we have a dedicated run button next to it */}
                            <WorkflowActionsDropdown workflow={workflow} showRunAction={false} buttonSize="md" />
                            {template ? (
                                <CreateFromTemplateActionButtons workflow={workflow} />
                            ) : (
                                <AutomationDetailsPageActionButtons workflow={workflow} />
                            )}
                        </div>
                    </div>
                    {/* Description and trigger info */}
                    <div className="flex flex-col gap-1">
                        <Text className="text-base text-content-strong">
                            {workflow.metadata?.description || "No description provided."}
                        </Text>
                        <AutomationPropertiesHeader
                            workflow={workflow}
                            onEdit={() => {
                                setIsWorkflowEditDialogOpen(true);
                            }}
                        />
                    </div>
                </div>
            ) : (
                <BreadcrumbsSkeleton itemsCount={2} />
            )}

            {isDeleting && (
                <Banner
                    variant="info"
                    className="mt-4"
                    text="This automation is being deleted. All interactions are disabled."
                />
            )}

            <ErrorMessage error={workflowError} />

            <Tabs className="flex grow flex-col" value={selectedTab} onValueChange={changeTab}>
                {!isEditingSteps && (
                    <TabsList className="mb-6 w-fit">
                        <TabsTrigger value={WorkflowDetailsTab.Executions}>Executions</TabsTrigger>
                        <TabsTrigger value={WorkflowDetailsTab.Definition}>Definition</TabsTrigger>
                    </TabsList>
                )}

                <TabsContent value={WorkflowDetailsTab.Definition} className="flex flex-col data-[state=active]:grow">
                    <AutomationDefinitionSection />
                </TabsContent>

                <TabsContent value={WorkflowDetailsTab.Executions} className="flex flex-col data-[state=active]:grow">
                    {workflow ? (
                        <WorkflowExecutionsList
                            executions={workflowExecutions || []}
                            members={members?.members}
                            workflow={workflow}
                            hasManualTrigger={hasManualTrigger(workflow)}
                            error={workflowExecutionsError}
                            onRetry={() => refetchExecutions()}
                        />
                    ) : null}
                </TabsContent>
            </Tabs>
            {workflow ? (
                <AutomationEditDialog
                    open={isWorkflowEditDialogOpen}
                    onOpenChange={setIsWorkflowEditDialogOpen}
                    onSubmit={template ? onAutomationEditDialogSubmit : undefined}
                    workflow={workflow}
                />
            ) : null}
        </div>
    );
};

/**
 * Sets of buttons displayed when creating a new automation from a template.
 */
const CreateFromTemplateActionButtons: FC<{ workflow: PlainWorkflow }> = ({ workflow }) => {
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    const navigate = useNavigate();

    const cancelAutomationCreation = () => {
        setIsEditingSteps(false);
        setEditableSteps(convertWorkflowActionToFormSteps(workflow.spec?.action));
        navigate(workflowsUrl());
    };

    return (
        <div className="flex items-center gap-2">
            <Button variant="outline" onClick={cancelAutomationCreation} data-tracking-id="cancel-automation-creation">
                Cancel
            </Button>
            <CreateAutomationButton workflow={workflow} />
        </div>
    );
};

const AutomationDetailsPageActionButtons: FC<{ workflow: PlainWorkflow }> = ({ workflow }) => {
    const isEditingSteps = useAtomValue(isEditingStepsAtom);
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    const location = useLocation();
    const navigate = useNavigate();

    const handleEdit = useCallback(() => {
        // if the user is on the executions tab and they click edit
        // take them to the definitions tab
        if (location.hash !== `#${WorkflowDetailsTab.Definition}`) {
            navigate(`#${WorkflowDetailsTab.Definition}`, { replace: true });
        }
        setIsEditingSteps(true);
    }, [setIsEditingSteps, navigate, location.hash]);

    const handleCancel = useCallback(() => {
        setIsEditingSteps(false);
        setEditableSteps(convertWorkflowActionToFormSteps(workflow.spec?.action));
    }, [setIsEditingSteps, setEditableSteps, workflow.spec?.action]);

    if (isEditingSteps) {
        return (
            <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleCancel} data-tracking-id="cancel">
                    Cancel
                </Button>
                <SaveAutomationStepsButton workflowId={workflow.id} />
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Button variant="outline" LeadingIcon={IconPencilWithSize} onClick={handleEdit} data-tracking-id="edit">
                Edit
            </Button>
            {hasManualTrigger(workflow) && <RunAutomationButton workflowId={workflow.id} data-tracking-id="run" />}
        </div>
    );
};
